<template>
  <div class="home-page">
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <h1>Revive your photos,<br>enhance your memories,<br><span>with AI upscale.</span></h1>
          <p>
            Transform your blurry or low-quality photos into stunning works of art with our AI photo enhancer.
            Restore lost details and improve quality with just a few clicks.
          </p>
          
          <div class="platform-badges">
            <a 
              href="javascript:void(0)" 
              class="platform-badge apple"
              @click="showComingSoonMessage"
            >
              <svg viewBox="0 0 24 24" class="apple-icon">
                <path d="M17.05,13.87c-0.14-1.5,0.41-2.97,1.52-3.97c-0.61-0.86-1.5-1.47-2.5-1.75c-1.03-0.27-2.15-0.08-3.44,0.27 c-1.44,0.39-2.12,0.39-2.12,0.39c-1.71,0-3.22-1.15-3.22-1.15c-1.8,0.06-3.52,1.67-3.92,3.44c-0.4,1.77,0.38,3.55,0.38,3.55 c0.65,1.5,1.64,3.09,2.88,4.63c1.24,1.54,2.44,2.31,3.34,2.31c0.9,0,2.05-0.9,3.48-0.9c1.43,0,2.25,0.9,3.34,0.9 c1.09,0,2.09-0.84,3.25-2.29c0.76-0.96,1.39-2.03,1.86-3.17C20.01,15.38,17.19,15.37,17.05,13.87z M14.51,4.62 c0.82-1,0.77-2.41,0.77-2.41s-1.63,0.08-2.69,1.12c-1.06,1.04-0.91,2.45-0.91,2.45S13.69,5.62,14.51,4.62z"/>
              </svg>
              <span>App Store</span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.fast.picture.blurclear" class="platform-badge android">
              <svg viewBox="0 0 24 24" class="android-icon">
                <path d="M6,18c0,0.55,0.45,1,1,1h1v3.5C8,23.33,8.67,24,9.5,24s1.5-0.67,1.5-1.5V19h2v3.5c0,0.83,0.67,1.5,1.5,1.5 s1.5-0.67,1.5-1.5V19h1c0.55,0,1-0.45,1-1V8H6V18z M3.5,8C2.67,8,2,8.67,2,9.5v7C2,17.33,2.67,18,3.5,18S5,17.33,5,16.5v-7 C5,8.67,4.33,8,3.5,8z M20.5,8c-0.83,0-1.5,0.67-1.5,1.5v7c0,0.83,0.67,1.5,1.5,1.5s1.5-0.67,1.5-1.5v-7C22,8.67,21.33,8,20.5,8z M15.53,2.16l1.3-1.3c0.2-0.2,0.2-0.51,0-0.71c-0.2-0.2-0.51-0.2-0.71,0l-1.48,1.48C13.85,1.23,12.95,1,12,1 c-0.96,0-1.86,0.23-2.66,0.63L7.85,0.15c-0.2-0.2-0.51-0.2-0.71,0c-0.2,0.2-0.2,0.51,0,0.71l1.31,1.31C6.97,3.26,6,5.01,6,7h12 C18,5.01,17.03,3.25,15.53,2.16z M10,5H9V4h1V5z M15,5h-1V4h1V5z"/>
              </svg>
              <span>Google Play</span>
            </a>
          </div>
        </div>
        
        <div class="hero-image">
          <img src="../assets/英语.png" alt="Photo Enhancement" class="hero-img" />
        </div>
      </div>
    </section>

    <section id="features" class="features-section">
      <div class="container">
        <h2 class="section-title">What wonders can our Photo Enhancer do?</h2>
        <p class="section-subtitle">Discover the powerful tools that bring your photos to life</p>
        
        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">✨</div>
            <h3>Photo Enhancer</h3>
            <p>Revive old photos with AI photo sharpener that enhances and restores blurry images to high-definition quality.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🔍</div>
            <h3>AI Upscale</h3>
            <p>Upgrade vintage or old phone photos to look like they were taken with the latest devices, and convert blurred photos to clear digital images.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🖼️</div>
            <h3>Image Enlarger</h3>
            <p>Enlarge your photos without losing quality. Our AI technology preserves details and sharpness even when scaling up to 4x the original size.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🌟</div>
            <h3>Image Restorer</h3>
            <p>Revive compressed, damaged, or low-resolution photos with intelligent restoration, including deblurring for high-definition results.</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🔄</div>
            <h3>Batch Processing</h3>
            <p>Save time by enhancing multiple photos at once with our efficient batch processing feature. Transform entire albums in minutes with just a few clicks!</p>
          </div>
          
          <div class="feature-card">
            <div class="feature-icon">🔒</div>
            <h3>Safe & Secure</h3>
            <p>We prioritize your privacy and security. Your photos are processed with the highest level of protection, and we never share your data with third parties.</p>
          </div>
        </div>
      </div>
    </section>

    <section class="testimonials">
      <div class="container">
        <h2 class="section-title">What Our Users Say</h2>
        <p class="section-subtitle">Hear from people who have transformed their photos with our technology</p>
        
        <div class="testimonial-cards">
          <div class="testimonial-card card">
            <div class="testimonial-content">
              <p>"This app is amazing! It brought my old family photos back to life with incredible detail. I'm truly impressed with the results."</p>
            </div>
            <div class="testimonial-author">
              <div class="author-avatar">
                <img src="../assets/英语2.png" alt="User Avatar" class="avatar-img" />
              </div>
              <div class="author-info">
                <h4>John Smith</h4>
                <p>Photography Enthusiast</p>
              </div>
            </div>
          </div>
          
          <div class="testimonial-card card">
            <div class="testimonial-content">
              <p>"I have many old family photos that are of poor quality, this tool helped me restore their clarity. Now I can clearly see what my grandparents looked like when they were young!"</p>
            </div>
            <div class="testimonial-author">
              <div class="author-avatar">
                <img src="../assets/英语3.png" alt="User Avatar" class="avatar-img" />
              </div>
              <div class="author-info">
                <h4>Emily Johnson</h4>
                <p>Family Album Collector</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="download-section">
      <div class="container">
        <div class="download-content">
          <h2 class="section-title">Don't settle for less than perfect photos</h2>
          <p class="section-subtitle">Download today and transform your memories into stunning works of art</p>
          
          <div class="download-badges">
            <a href="#" class="download-badge apple">
              <svg viewBox="0 0 24 24" class="apple-icon">
                <path d="M17.05,13.87c-0.14-1.5,0.41-2.97,1.52-3.97c-0.61-0.86-1.5-1.47-2.5-1.75c-1.03-0.27-2.15-0.08-3.44,0.27 c-1.44,0.39-2.12,0.39-2.12,0.39c-1.71,0-3.22-1.15-3.22-1.15c-1.8,0.06-3.52,1.67-3.92,3.44c-0.4,1.77,0.38,3.55,0.38,3.55 c0.65,1.5,1.64,3.09,2.88,4.63c1.24,1.54,2.44,2.31,3.34,2.31c0.9,0,2.05-0.9,3.48-0.9c1.43,0,2.25,0.9,3.34,0.9 c1.09,0,2.09-0.84,3.25-2.29c0.76-0.96,1.39-2.03,1.86-3.17C20.01,15.38,17.19,15.37,17.05,13.87z M14.51,4.62 c0.82-1,0.77-2.41,0.77-2.41s-1.63,0.08-2.69,1.12c-1.06,1.04-0.91,2.45-0.91,2.45S13.69,5.62,14.51,4.62z"/>
              </svg>
              <span>App Store</span>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.fast.picture.blurclear" class="download-badge android">
              <svg viewBox="0 0 24 24" class="android-icon">
                <path d="M6,18c0,0.55,0.45,1,1,1h1v3.5C8,23.33,8.67,24,9.5,24s1.5-0.67,1.5-1.5V19h2v3.5c0,0.83,0.67,1.5,1.5,1.5 s1.5-0.67,1.5-1.5V19h1c0.55,0,1-0.45,1-1V8H6V18z M3.5,8C2.67,8,2,8.67,2,9.5v7C2,17.33,2.67,18,3.5,18S5,17.33,5,16.5v-7 C5,8.67,4.33,8,3.5,8z M20.5,8c-0.83,0-1.5,0.67-1.5,1.5v7c0,0.83,0.67,1.5,1.5,1.5s1.5-0.67,1.5-1.5v-7C22,8.67,21.33,8,20.5,8z M15.53,2.16l1.3-1.3c0.2-0.2,0.2-0.51,0-0.71c-0.2-0.2-0.51-0.2-0.71,0l-1.48,1.48C13.85,1.23,12.95,1,12,1 c-0.96,0-1.86,0.23-2.66,0.63L7.85,0.15c-0.2-0.2-0.51-0.2-0.71,0c-0.2,0.2-0.2,0.51,0,0.71l1.31,1.31C6.97,3.26,6,5.01,6,7h12 C18,5.01,17.03,3.25,15.53,2.16z M10,5H9V4h1V5z M15,5h-1V4h1V5z"/>
              </svg>
              <span>Google Play</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- 添加新的提示样式 -->
    <transition name="fade-scale">
      <div v-if="showMessage" class="modern-tooltip">
        <div class="tooltip-content">
          <div class="tooltip-icon">
            <span class="emoji">🚀</span>
            <div class="sparkles"></div>
          </div>
          <div class="tooltip-text">
            <h4>Coming to iOS!</h4>
            <p>We're crafting something amazing for Apple users.<br>Stay tuned for the magic! ✨</p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  components: {
    // 移除 FeatureCard 组件的注册
  },
  data() {
    return {
      showMessage: false
    }
  },
  methods: {
    showComingSoonMessage() {
      // 修改下载区域的Apple按钮点击事件
      const appleButtons = document.querySelectorAll('.apple')
      appleButtons.forEach(button => {
        button.href = 'javascript:void(0)'
        button.addEventListener('click', () => {
          this.showMessage = true
          setTimeout(() => {
            this.showMessage = false
          }, 3000)
        })
      })
    }
  },
  mounted() {
    this.showComingSoonMessage()
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  .hero {
    padding: 100px 0;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    overflow: hidden;
    
    .container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 40px;
      position: relative;
    }
    
    .hero-content {
      flex: 0 0 55%;
      max-width: 550px;
      z-index: 2;
      
      h1 {
        font-size: 3.5rem;
        font-weight: 800;
        margin-bottom: 20px;
        line-height: 1.2;
        color: var(--text-color);
        
        span {
          color: var(--primary-color);
        }
      }
      
      p {
        font-size: 1.2rem;
        line-height: 1.6;
        color: var(--text-light);
        margin-bottom: 30px;
      }
      
      .platform-badges {
        display: flex;
        gap: 15px;
        margin-top: 30px;
      }
      
      .platform-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 140px;
        height: 45px;
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        text-decoration: none;
        padding: 0 12px;
        
        &:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
        }
        
        &.apple {
          .apple-icon {
            width: 18px;
            height: 18px;
            fill: #000;
            margin-right: 8px;
          }
          
          span {
            font-size: 14px;
            font-weight: 600;
            color: #000;
          }
        }
        
        &.android {
          .android-icon {
            width: 18px;
            height: 18px;
            fill: #3DDC84;
            margin-right: 8px;
          }
          
          span {
            font-size: 14px;
            font-weight: 600;
            color: #000;
          }
        }
      }
    }
    
    .hero-image {
      flex: 0 0 45%;
      display: flex;
      justify-content: flex-end;
      position: relative;
      
      .hero-img {
        width: 100%;
        max-width: 550px;
        border-radius: 12px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        transition: transform 0.3s ease;
        transform: translateX(0) scale(1);
        
        &:hover {
          transform: translateY(-10px);
        }
      }
    }
  }
  
  .features-section {
    padding: 100px 0;
    background-color: #f8f9fa;
    
    .section-title {
      text-align: center;
      font-size: 2.5rem;
      margin-bottom: 20px;
    }
    
    .section-subtitle {
      text-align: center;
      font-size: 1.2rem;
      color: var(--text-light);
      max-width: 700px;
      margin: 0 auto 50px;
    }
    
    .features-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 30px;
      
      .feature-card {
        background: white;
        border-radius: 12px;
        padding: 30px;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        text-align: center;
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
        }
        
        .feature-icon {
          font-size: 48px;
          margin-bottom: 20px;
          color: var(--primary-color);
        }
        
        h3 {
          font-size: 20px;
          margin-bottom: 15px;
          color: var(--text-color);
        }
        
        p {
          color: var(--text-light);
          line-height: 1.6;
        }
      }
    }
  }
  
  .testimonials {
    padding: 100px 0;
    
    .testimonial-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 30px;
      margin-top: 50px;
    }
    
    .testimonial-card {
      padding: 30px;
      
      .testimonial-content {
        margin-bottom: 20px;
        
        p {
          color: var(--text-light);
          font-style: italic;
          line-height: 1.8;
          position: relative;
          
          &::before {
            content: '"';
            font-size: 60px;
            color: rgba(var(--primary-color-rgb), 0.1);
            position: absolute;
            top: -20px;
            left: -15px;
            font-family: serif;
          }
        }
      }
      
      .testimonial-author {
        display: flex;
        align-items: center;
        
        .author-avatar {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          overflow: hidden;
          margin-right: 15px;
          
          .avatar-img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }
        
        .author-info {
          h4 {
            font-size: 16px;
            margin-bottom: 5px;
            color: var(--text-color);
          }
          
          p {
            font-size: 14px;
            color: var(--text-light);
          }
        }
      }
    }
  }
  
  .download-section {
    padding: 100px 0;
    background: linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%);
    position: relative;
    color: white;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.03);
      opacity: 0.1;
      z-index: 1;
    }
    
    .container {
      position: relative;
      z-index: 2;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    
    .download-content {
      text-align: center;
      max-width: 800px;
      margin: 0 auto;
      
      .section-title {
        font-size: 2.8rem;
        font-weight: 700;
        margin-bottom: 20px;
        background: linear-gradient(to right, #fff, #e0e0e0);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      }
      
      .section-subtitle {
        font-size: 1.3rem;
        margin-bottom: 40px;
        color: rgba(255, 255, 255, 0.9);
        line-height: 1.6;
      }
    }
    
    .download-badges {
      display: flex;
      justify-content: center;
      gap: 20px;
      
      .download-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 160px;
        height: 50px;
        background-color: white;
        border-radius: 10px;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
        transition: all 0.3s ease;
        text-decoration: none;
        padding: 0 15px;
        
        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
        }
        
        &.apple {
          .apple-icon {
            width: 22px;
            height: 22px;
            fill: #000;
            margin-right: 10px;
          }
          
          span {
            font-size: 16px;
            font-weight: 600;
            color: #000;
          }
        }
        
        &.android {
          .android-icon {
            width: 22px;
            height: 22px;
            fill: #3DDC84;
            margin-right: 10px;
          }
          
          span {
            font-size: 16px;
            font-weight: 600;
            color: #000;
          }
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .home-page {
    .hero {
      .hero-image {
        .hero-img {
          width: 100%;
          transform: translateX(0) scale(1);
          
          &:hover {
            transform: translateY(-10px);
          }
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .home-page {
    .hero {
      .container {
        flex-direction: column;
        text-align: center;
      }
      
      .hero-content {
        flex: 0 0 100%;
        max-width: 600px;
        margin-bottom: 50px;
      }
      
      .hero-image {
        flex: 0 0 100%;
        justify-content: center;
        
        .hero-img {
          width: 100%;
          max-width: 700px;
          transform: translateX(0) scale(1);
          
          &:hover {
            transform: translateX(0) scale(1.02);
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .home-page {
    .hero {
      padding: 60px 0;
      
      .hero-content {
        h1 {
          font-size: 2.5rem;
        }
      }
      
      .hero-image {
        .hero-img {
          max-width: 100%;
        }
      }
    }
  }
}

.modern-tooltip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(12px);
  border-radius: 20px;
  padding: 25px;
  z-index: 1000;
  box-shadow: 
    0 0 0 1px rgba(255, 255, 255, 0.2),
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.08),
    0 0 50px rgba(82, 109, 255, 0.25);
  min-width: 320px;
  border: 1px solid rgba(255, 255, 255, 0.2);

  .tooltip-content {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .tooltip-icon {
    position: relative;
    
    .emoji {
      font-size: 3em;
      animation: float 2s ease-in-out infinite;
      display: block;
    }

    .sparkles {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      animation: sparkle 1.5s linear infinite;
    }
  }

  .tooltip-text {
    h4 {
      color: #fff;
      font-size: 1.4em;
      margin: 0 0 8px 0;
      font-weight: 700;
      background: linear-gradient(45deg, #fff, #69b4ff);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
    }

    p {
      color: rgba(255, 255, 255, 0.95);
      margin: 0;
      font-size: 1.1em;
      line-height: 1.5;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes sparkle {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.85);
}

@keyframes glow {
  0%, 100% {
    box-shadow: 
      0 0 0 1px rgba(255, 255, 255, 0.2),
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 0 50px rgba(82, 109, 255, 0.25);
  }
  50% {
    box-shadow: 
      0 0 0 1px rgba(255, 255, 255, 0.3),
      0 4px 6px rgba(0, 0, 0, 0.1),
      0 1px 3px rgba(0, 0, 0, 0.08),
      0 0 80px rgba(82, 109, 255, 0.45);
  }
}

.modern-tooltip {
  animation: glow 2s ease-in-out infinite;
}
</style> 